<div class="add-member-dialog p-4 bg-white rounded-4">
  <div class="dialog-header d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
    <div class="d-flex align-items-center gap-3">
      <div class="dialog-header-icon">
        <mat-icon>person_add</mat-icon>
      </div>
      <div>
        <h3 class="dialog-title mb-0 fw-bold">{{ isEditMode ? 'Edit Member' : 'Add Member' }}</h3>
        <p class="dialog-subtitle mb-0">Fill in member details and property assignment below.</p>
      </div>
    </div>
    <button type="button" mat-icon-button class="dialog-close-btn flex-shrink-0" (click)="closeDialog()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div mat-dialog-content class="dialog-content px-0">
    <form [formGroup]="addMemberForm" #formDirective="ngForm" (ngSubmit)="onSubmit(formDirective)" class="member-form">
      
      <!-- Section 1: Member Personal Info -->
      <div class="dialog-section-bar mb-3">
        <div class="section-icon-circle">
          <mat-icon>person</mat-icon>
        </div>
        <h4 class="section-bar-title mb-0">Personal Details</h4>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- First Name -->
        <div class="form-group half-width flex-fill">
          <label for="first-name" class="form-label fw-semibold">
            First Name <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.controls['first_name'].invalid && addMemberForm.controls['first_name'].touched">
            <input id="first-name" class="w-100 form-control" type="text" formControlName="first_name" placeholder="Enter First Name">
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.controls['first_name'].invalid && addMemberForm.controls['first_name'].touched">
            First Name is required
          </span>
        </div>

        <!-- Last Name -->
        <div class="form-group half-width flex-fill">
          <label for="last-name" class="form-label fw-semibold">
            Last Name <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.controls['last_name'].invalid && addMemberForm.controls['last_name'].touched">
            <input id="last-name" class="w-100 form-control" type="text" formControlName="last_name" placeholder="Enter Last Name">
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.controls['last_name'].invalid && addMemberForm.controls['last_name'].touched">
            Last Name is required
          </span>
        </div>
      </div>

      <!-- Section 2: Property & Assignment Info -->
      <div class="dialog-section-bar mb-3 mt-3">
        <div class="section-icon-circle">
          <mat-icon>apartment</mat-icon>
        </div>
        <h4 class="section-bar-title mb-0">Property & Role Details</h4>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- Project -->
        <div class="form-group half-width flex-fill">
          <label for="project-select" class="form-label fw-semibold">
            Project <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.get('account_id')?.invalid && addMemberForm.get('account_id')?.touched">
            <app-searchable-dropdown
              id="project-select"
              formControlName="account_id"
              [options]="projectOptions | searchableOptions:'account_name':'account_id'"
              placeholder="Select Project"
              class="w-100"
              (selectionChange)="onProjectChange({ value: $event })">
            </app-searchable-dropdown>
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.get('account_id')?.invalid && addMemberForm.get('account_id')?.touched">
            Project is required
          </span>
        </div>

        <!-- Wing -->
        <div class="form-group half-width flex-fill">
          <label for="wing-select" class="form-label fw-semibold">
            Wing / Block <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.get('block_id')?.invalid && addMemberForm.get('block_id')?.touched">
            <app-searchable-dropdown
              id="wing-select"
              formControlName="block_id"
              [options]="wingOptions | searchableOptions:'block_name':'block_id'"
              placeholder="Select Wing"
              class="w-100"
              [disabled]="!selectedProject"
              (selectionChange)="onWingChange({ value: $event })">
            </app-searchable-dropdown>
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.get('block_id')?.invalid && addMemberForm.get('block_id')?.touched">
            Wing is required
          </span>
        </div>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- Floor -->
        <div class="form-group half-width flex-fill">
          <label for="floor-select" class="form-label fw-semibold">
            Floor <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.get('floor_id')?.invalid && addMemberForm.get('floor_id')?.touched">
            <app-searchable-dropdown
              id="floor-select"
              formControlName="floor_id"
              [options]="floorOptions | searchableOptions:'floor_name':'floor_id'"
              placeholder="Select Floor"
              class="w-100"
              [disabled]="!selectedWing"
              (selectionChange)="onFloorChange({ value: $event })">
            </app-searchable-dropdown>
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.get('floor_id')?.invalid && addMemberForm.get('floor_id')?.touched">
            Floor is required
          </span>
        </div>

        <!-- Unit -->
        <div class="form-group half-width flex-fill">
          <label for="unit-select" class="form-label fw-semibold">
            Unit / Flat <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.get('floor_unit_id')?.invalid && addMemberForm.get('floor_unit_id')?.touched">
            <app-searchable-dropdown
              id="unit-select"
              formControlName="floor_unit_id"
              [options]="unitOptions | searchableOptions:'floor_unit':'floor_unit_id'"
              placeholder="Select Unit"
              class="w-100"
              [disabled]="!selectedFloor"
              (selectionChange)="onFloorUnitChange({ value: $event })">
            </app-searchable-dropdown>
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.get('floor_unit_id')?.invalid && addMemberForm.get('floor_unit_id')?.touched">
            Unit is required
          </span>
        </div>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- Role -->
        <div class="form-group half-width flex-fill">
          <label for="role-select" class="form-label fw-semibold">
            Role <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.get('role_id')?.invalid && addMemberForm.get('role_id')?.touched">
            <app-searchable-dropdown
              id="role-select"
              formControlName="role_id"
              [options]="roleOptions | searchableOptions:'role':'role_id'"
              placeholder="Select Role"
              class="w-100">
            </app-searchable-dropdown>
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.get('role_id')?.invalid && addMemberForm.get('role_id')?.touched">
            Role is required
          </span>
        </div>

        <!-- Type (Owner / Renter) -->
        <div class="form-group half-width flex-fill">
          <label class="form-label fw-semibold">Member Type <span class="text-danger">*</span></label>
          <div class="radio-container p-2 border rounded-3 bg-white">
            <mat-radio-group formControlName="is_owner" class="d-flex flex-row gap-4 align-items-center h-100">
              <mat-radio-button value="Owner" color="primary">Owner</mat-radio-button>
              <mat-radio-button value="Renter" color="primary">Renter</mat-radio-button>
            </mat-radio-group>
          </div>
        </div>
      </div>

      <!-- Section 3: Contact & Credentials -->
      <div class="dialog-section-bar mb-3 mt-3">
        <div class="section-icon-circle">
          <mat-icon>contacts</mat-icon>
        </div>
        <h4 class="section-bar-title mb-0">Account & Contact Info</h4>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- Username -->
        <div class="form-group half-width flex-fill">
          <label for="username" class="form-label fw-semibold">
            Username <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.controls['username'].invalid && addMemberForm.controls['username'].touched">
            <input id="username" class="w-100 form-control" type="text" formControlName="username" placeholder="Enter Username">
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.controls['username'].invalid && addMemberForm.controls['username'].touched">
            Username is required
          </span>
        </div>

        <!-- Email -->
        <div class="form-group half-width flex-fill">
          <label for="email-id" class="form-label fw-semibold">Email Address</label>
          <div class="form-field" [class.error]="addMemberForm.controls['email_id'].invalid && addMemberForm.controls['email_id'].touched">
            <input id="email-id" class="w-100 form-control" type="email" formControlName="email_id" placeholder="Enter Email">
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.controls['email_id'].invalid && addMemberForm.controls['email_id'].touched">
            Invalid email address
          </span>
        </div>
      </div>

      <div class="form-row d-flex gap-2 mb-3">
        <!-- Mobile -->
        <div class="form-group half-width flex-fill">
          <label for="mob-no" class="form-label fw-semibold">
            Mobile Number <span class="text-danger">*</span>
          </label>
          <div class="form-field" [class.error]="addMemberForm.controls['mob_no'].invalid && addMemberForm.controls['mob_no'].touched">
            <input id="mob-no" class="w-100 form-control" type="text" formControlName="mob_no" placeholder="Enter 10 digit Mobile No">
          </div>
          <span class="error-text text-danger small" *ngIf="addMemberForm.controls['mob_no'].invalid && addMemberForm.controls['mob_no'].touched">
            Valid 10-digit mobile number is required
          </span>
        </div>

        <!-- City & State -->
        <div class="form-group half-width flex-fill">
          <label for="city" class="form-label fw-semibold">City / State</label>
          <div class="d-flex gap-2">
            <div class="form-field flex-fill">
              <input id="city" class="w-100 form-control" type="text" formControlName="city" placeholder="City">
            </div>
            <div class="form-field flex-fill">
              <input id="state" class="w-100 form-control" type="text" formControlName="state" placeholder="State">
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Picture Section -->
      <div class="form-group mb-3">
        <label class="form-label fw-semibold">Profile Picture</label>
        <div class="profile-upload-box">
          <div class="profile-preview-wrapper" *ngIf="profilePicPreview">
            <img [src]="profilePicPreview" alt="Profile Preview" class="preview-img" (error)="handleImageError($event)">
            <button type="button" mat-icon-button color="warn" class="remove-pic-btn" (click)="removeProfilePic()" matTooltip="Remove Photo">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="upload-trigger-zone" *ngIf="!profilePicPreview" (click)="profilePicInput.click()">
            <input #profilePicInput type="file" id="profilePic" accept="image/*" (change)="onProfilePicSelected($event)" class="d-none">
            <mat-icon class="upload-icon">add_a_photo</mat-icon>
            <span class="upload-text">{{ isEditMode ? 'Change Profile Picture' : 'Upload Profile Picture' }}</span>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div class="d-flex justify-content-end gap-2 pt-3 border-top mt-3">
    <button type="button" class="btn btn-outline-secondary rounded-pill px-4" (click)="closeDialog()">Cancel</button>
    <button type="button" class="gold-button" (click)="onSubmit(formDirective)" [disabled]="addMemberForm.invalid">
      {{ isEditMode ? 'Update Member' : 'Save Member' }}
    </button>
  </div>
</div>
